import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, CheckCircle, Play, Building2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, getCurrentUser } from '../utils/api';

const Dashboard = () => {
  const { print_lang, currentLanguage } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = getCurrentUser();

  useEffect(() => {
    loadTodayAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTodayAppointments = async () => {
    try {
      const today = new Date();
      const from = today.toISOString().split('T')[0] + ' 00:00:00';
      const to = today.toISOString().split('T')[0] + ' 23:59:59';

      const data = await fetchWithAuth(`/api/dentists/my-appointments?from=${from}&to=${to}`);
      if (Array.isArray(data)) {
        setAppointments(data.filter(apt => apt.patient_id)); // Only patient appointments
      }
    } catch (error) {
      console.error('Failed to load appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = {
    total: appointments.length,
    waiting: appointments.filter(a => a.status === 'checked_in').length,
    inProgress: appointments.filter(a => a.status === 'in_progress').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    scheduled: appointments.filter(a => a.status === 'scheduled').length
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{print_lang('scheduled', 'Scheduled')}</span>;
      case 'checked_in':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">{print_lang('waiting', 'Waiting')}</span>;
      case 'in_progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">{print_lang('in_progress', 'In Progress')}</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">{print_lang('completed', 'Completed')}</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">
          {print_lang('welcome', 'Welcome')}, {user?.name}!
        </h2>
        <p className="text-blue-100">
          {new Date().toLocaleDateString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
              <p className="text-sm text-gray-500">{print_lang('today_appointments', "Today's Appointments")}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.waiting}</p>
              <p className="text-sm text-gray-500">{print_lang('waiting', 'Waiting')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Play className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.inProgress}</p>
              <p className="text-sm text-gray-500">{print_lang('in_progress', 'In Progress')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.completed}</p>
              <p className="text-sm text-gray-500">{print_lang('completed', 'Completed')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Appointments */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">
            {print_lang('today_appointments', "Today's Appointments")}
          </h3>
        </div>

        {appointments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{print_lang('no_appointments_today', 'No appointments today')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {appointments
              .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
              .map(apt => (
                <div key={apt.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[60px]">
                        <p className="text-lg font-bold text-blue-600">{formatTime(apt.start_date)}</p>
                        <p className="text-xs text-gray-400">{formatTime(apt.end_date)}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-800">{apt.patient_name || 'Unknown'}</p>
                          {getStatusBadge(apt.status)}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span>{currentLanguage === 'th' ? (apt.type_name_th || apt.type_name || apt.text) : (apt.type_name || apt.text)}</span>
                          {apt.branch_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {apt.branch_name}
                            </span>
                          )}
                        </div>
                        {/* Medical Alerts */}
                        {(apt.drug_allergy || apt.medical_history) && (
                          <div className="flex gap-2 mt-2">
                            {apt.drug_allergy && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                                <AlertTriangle className="w-3 h-3" />
                                {print_lang('drug_allergy', 'Allergy')}: {apt.drug_allergy}
                              </span>
                            )}
                            {apt.medical_history && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                                <AlertTriangle className="w-3 h-3" />
                                {print_lang('medical_history', 'Medical')}: {apt.medical_history.substring(0, 30)}...
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {apt.patient_phone && (
                        <a href={`tel:${apt.patient_phone}`} className="text-gray-400 hover:text-gray-600">
                          {apt.patient_phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Branches Info */}
      {user?.branches && user.branches.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800">
              {print_lang('working_branches', 'Working Branches')}
            </h3>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {user.branches.map(branch => (
                <span
                  key={branch.branch_id}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm
                    ${branch.branch_id === user.branch_id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                    }`}
                >
                  <Building2 className="w-4 h-4" />
                  {branch.branch_name}
                  {branch.branch_id === user.branch_id && (
                    <span className="text-xs">({print_lang('current', 'Current')})</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
